import { isEmpty, isUndefined } from "lodash";
import { RiExternalLinkLine } from "react-icons/ri";
import useGAEvent from "@/hooks/useGAEvent";
import { getDisplayPrice } from "@/helpers/helpers";

const REALTOR_SEARCH_URL = "https://www.realtor.com/realestateandhomes-search";

interface SimilarPropertiesProps {
    property: Record<string, any>;
    isAuthenticated: boolean;
}

const SimilarProperties: React.FC<SimilarPropertiesProps> = ({ property, isAuthenticated }) => {
    const { sendEvent } = useGAEvent();

    /**
     * Generates a URL to search for similar properties based on the provided property object.
     *
     * @param property - The property object containing details of the current property.
     *
     * @returns The URL for searching similar properties.
     */
    const generateSimilarPropertiesURLQuery = (property: Record<string, any>) => {
        // Guard clause for empty property
        if (!property) return REALTOR_SEARCH_URL;

        let type = "";

        const zipCode = property?.zipCode ?? property?.zip_code;
        const zipcode = !isUndefined(zipCode) && !isEmpty(String(zipCode)) ? `${zipCode}/` : "";

        const bedrooms =
            !isUndefined(property?.bedrooms) && !isEmpty(property.bedrooms?.toString())
                ? `beds-${property.bedrooms}-${property.bedrooms}/`
                : "";

        const bathrooms =
            !isUndefined(property?.bathrooms) && !isEmpty(property.bathrooms)
                ? `baths-${property.bathrooms}-${property.bathrooms}/`
                : "";

        const priceFrom = Number(getDisplayPrice(property) ?? 0) * 0.8;
        const priceTo = Number(getDisplayPrice(property) ?? 0) * 1.2;
        const priceRange = `price-${priceFrom}-${priceTo}`;

        const propertyType = property?.propertyType ?? property?.property_type;
        if (propertyType === "Condominium") {
            type = "type-condo";
        } else if (propertyType === "Single Family Residential") {
            type = "type-single-family-home";
        } else {
            type = "type-condo";
        }

        return `${REALTOR_SEARCH_URL}/${zipcode}${type}/${bedrooms}${bathrooms}${priceRange}`;
    };

    /**
     * Handle similar properties button click
     */
    const handleClick = () => {
        sendEvent("similar_property_click", {
            isAuthenticated,
            referralCode: localStorage.getItem("referral_code") ?? undefined,
            mlsId: (property.mlsId ?? property.mls_id) ?? undefined,
        });
    };

    return (
        <>
            <a
                id="see-similar-properties-btn"
                onClick={handleClick}
                href={generateSimilarPropertiesURLQuery(property)}
                target="_blank"
                rel="noopener noreferrer"
                className="app-button d-flex justify-content-center align-items-center gap-1 p-0 w-100 py-1 px-4 purple-bordered "
            >
                <span>See similar homes for sale now</span> <RiExternalLinkLine fontSize={18} />
            </a>
        </>
    );
};

export default SimilarProperties;
